import React from "react";

/**
 * ProjectsTableEVSPI (updated)
 * Columns: ID | Name | Owner | 기성률(EV%) | 진도율(Progress bar) | SPI | Due | Open
 * - 기성률: 숫자만 (예: 30%)
 * - 진도율: 기존과 동일한 bar + 퍼센트 라벨 (p.progress 기반)
 * - SPI: 숫자 (예: 0.70)
 */

export type ProjectRow = {
  id: string | number;
  name: string;
  owner: string; // display owner label (e.g., company name)
  due?: string;  // formatted date string
  evPercent?: number; // 0..100 (기성률, 누적 EV%)
  pvPercent?: number; // 0..100 (누적 PV%) — SPI 계산에 사용
  progress?: number;  // 0..1 or 0..100 (진도율, bar 표시용)
};

export interface ProjectsTableEVSPIProps<T extends ProjectRow = ProjectRow> {
  title?: string;
  projects: T[];
  className?: string;
  onOpen?: (project: T) => void;
  onViewAll?: () => void;
}

function clampPercent(v: number) {
  return Math.max(0, Math.min(100, v));
}

function normalizeProgress(p: ProjectRow): number | null {
  if (typeof p.progress === "number" && isFinite(p.progress)) {
    const val = p.progress <= 1 ? p.progress * 100 : p.progress;
    return clampPercent(val);
  }
  // fallback: evPercent를 진도율로 사용 (데이터가 없는 경우 대비)
  if (typeof p.evPercent === "number" && isFinite(p.evPercent)) {
    return clampPercent(p.evPercent);
  }
  return null;
}

function normalizeEV(p: ProjectRow): number | null {
  if (typeof p.evPercent === "number" && isFinite(p.evPercent)) {
    return clampPercent(p.evPercent);
  }
  // fallback: progress 사용 (bar와 같은 수치로 숫자만 보여줌)
  if (typeof p.progress === "number" && isFinite(p.progress)) {
    const val = p.progress <= 1 ? p.progress * 100 : p.progress;
    return clampPercent(val);
  }
  return null;
}

function getPV(p: ProjectRow): number | null {
  if (typeof p.pvPercent === "number" && isFinite(p.pvPercent)) {
    return clampPercent(p.pvPercent);
  }
  return null;
}

function computeSPI(p: ProjectRow): number | null {
  const ev = normalizeEV(p);
  const pv = getPV(p);
  if (ev == null || pv == null || pv <= 0) return null;
  return ev / pv;
}

export default function ProjectsTableEVSPI<T extends ProjectRow = ProjectRow>({
  title = "Active Projects",
  projects,
  className,
  onOpen,
  onViewAll,
}: ProjectsTableEVSPIProps<T>) {
  return (
    <section className={"rounded-2xl border bg-white " + (className ?? "")}>
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h2 className="text-sm font-medium">{title}</h2>
        <button
          type="button"
          className="text-xs rounded-lg border px-2.5 py-1 hover:bg-gray-50"
          onClick={onViewAll}
        >
          View all
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="px-4 py-2">ID</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Owner</th>
              <th className="px-4 py-2">기성률</th>
              <th className="px-4 py-2">진도율</th>
              <th className="px-4 py-2">SPI</th>
              <th className="px-4 py-2">Due Date</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => {
              const ev = normalizeEV(p);              // 기성률 숫자
              const progress = normalizeProgress(p);  // 진도율 bar
              const spi = computeSPI(p);              // 숫자
              return (
                <tr key={p.id} className="border-t">
                  <td className="px-4 py-3 font-mono text-xs">{String(p.id)}</td>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3">{p.owner}</td>

                  {/* 기성률: 숫자만 */}
                  <td className="px-4 py-3">
                    <span className="text-sm">{ev == null ? "—" : `${Math.round(ev)}%`}</span>
                  </td>

                  {/* 진도율: 기존 스타일(bar + % 라벨) */}
                  <td className="px-4 py-3">
                    <div className="w-44 h-2 rounded-full bg-gray-200" aria-label="progress bar">
                      <div className="h-2 rounded-full bg-gray-900" style={{ width: `${progress ?? 0}%` }} />
                    </div>
                    <span className="ml-2 align-middle text-xs text-gray-500">
                      {progress == null ? "—" : `${Math.round(progress)}%`}
                    </span>
                  </td>

                  {/* SPI: 숫자(예: 0.70) */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-800">{spi == null ? "—" : spi.toFixed(2)}</span>
                  </td>

                  <td className="px-4 py-3 text-gray-600">{p.due ?? ""}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="text-xs rounded-lg border px-2.5 py-1 hover:bg-gray-50"
                      onClick={() => onOpen?.(p as T)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
